import type { z } from 'zod/v4';
import type { saveManagedDocumentDraftBody } from './generated/api';

type GeneratedDocument = z.infer<typeof saveManagedDocumentDraftBody>['content'];
type Node = Omit<GeneratedDocument['content'][number], 'content'> & { content?: Node[] };
export type SectionDocument = Omit<GeneratedDocument, 'content' | 'layout'> & {
  content: Node[];
  layout?: Omit<NonNullable<GeneratedDocument['layout']>, 'header' | 'footer'> & { header: Node[]; footer: Node[] };
};
export type DocumentSections = NonNullable<SectionDocument['sections']>;
export type DocumentSection = DocumentSections['items'][number];
export function sameDocumentSectionValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((value, i) => sameDocumentSectionValue(value, b[i]));
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object' || Array.isArray(a) || Array.isArray(b)) return false;
  const left = Object.entries(a).filter(([, value]) => value !== undefined), right = Object.entries(b).filter(([, value]) => value !== undefined);
  return left.length === right.length && left.every(([key, value]) => Object.prototype.hasOwnProperty.call(b, key) && sameDocumentSectionValue(value, Reflect.get(b, key)));
}
const same = sameDocumentSectionValue;
export const sectionRules = { allowAdd: true, allowRename: true, allowReorder: true, allowHide: true };
type Part = { heading: Node; prefix: Node[]; content: Node[] };

/** Stable flat partitions. In independent mode ordinary headings have no structural meaning. */
export function splitDocumentSections(content: SectionDocument) {
  const independent = content.sections?.boundaryMode === 'independent';
  const preamble: Node[] = [], parts = new Map<string, Part>();
  let current: Part | undefined;
  for (const node of content.content) {
    const boundary = independent ? node.type === 'sectionBoundary' : node.type === 'heading' && node.attrs?.level === 2;
    const id = boundary ? node.attrs?.sectionId : undefined;
    if (typeof id === 'string') {
      if (parts.has(id)) throw new Error('Section identities must be unique.');
      current = { heading: node, prefix: [node], content: [] }; parts.set(id, current);
    } else if (independent && current && node.attrs?.sectionTitleId) {
      if (current.prefix.length !== 1 || current.content.length || current.prefix[0]?.attrs?.sectionId !== node.attrs.sectionTitleId) throw new Error('Structural labels must immediately follow their boundary.');
      current.heading = node; current.prefix.push(node);
    } else if (current) current.content.push(node);
    else preamble.push(node);
  }
  return { preamble, parts };
}
export function validateDocumentSections(content: SectionDocument) {
  const binding = content.sections, independent = binding?.boundaryMode === 'independent';
  const structural = (node: Node): boolean => node.type === 'sectionBoundary' || Boolean(node.attrs?.sectionId || node.attrs?.sectionTitleId) || Boolean(node.content?.some(structural));
  if (!binding) {
    if (content.content.some(structural)) throw new Error('Section headings require a section outline.');
    return;
  }
  const { parts } = splitDocumentSections(content);
  if (new Set(binding.items.map(s => s.id)).size !== binding.items.length || parts.size !== binding.items.length
    || !same([...parts.keys()], binding.items.map(s => s.id))) throw new Error('The section outline and document headings do not match.');
  const visit = (node: Node, top: boolean): void => {
    if (node.type === 'sectionBoundary' && (!independent || !top || !parts.has(String(node.attrs?.sectionId)) || node.content?.length || node.text || node.marks?.length || Object.keys(node.attrs ?? {}).some(k => k !== 'sectionId'))) throw new Error('Invalid independent section boundary.');
    if (node.attrs?.sectionTitleId && (!independent || !top || !['heading','paragraph'].includes(node.type) || !parts.has(String(node.attrs.sectionTitleId)) || node.attrs.sectionId)) throw new Error('Invalid structural section label.');
    if (node.attrs?.sectionId && (!top || !(independent ? node.type === 'sectionBoundary' : node.type === 'heading' && node.attrs.level === 2) || !parts.has(String(node.attrs.sectionId)))) throw new Error('Only a section boundary may carry its identity.');
    if (!independent && top && node.type === 'heading' && node.attrs?.level === 2 && !node.attrs.sectionId) throw new Error('Every top-level section heading requires an identity.');
    node.content?.forEach(child => visit(child, false));
  };
  content.content.forEach(n => visit(n, true));
  for (const section of binding.items) {
    const part = parts.get(section.id)!;
    if (independent ? part.prefix.length !== 2 || part.heading.attrs?.sectionTitleId !== section.id
      : part.heading.content?.map(n => n.text ?? '').join('') !== section.title) throw new Error('Section titles must match their boundaries.');
  }
}
export function restructureDocumentSections(content: SectionDocument, sections: DocumentSections): SectionDocument {
  const { preamble, parts } = splitDocumentSections(content), independent = content.sections?.boundaryMode === 'independent';
  const body = [...preamble];
  for (const item of sections.items) {
    const old = parts.get(item.id);
    if (independent) body.push(...(old?.prefix.map(node => node.attrs?.sectionTitleId && content.sections?.items.find(s => s.id === item.id)?.title !== item.title ? { ...node, content: [{ type: 'text', text: item.title }] } : node) ?? [
      { type: 'sectionBoundary', attrs: { sectionId: item.id } },
      { type: 'paragraph', attrs: { sectionTitleId: item.id }, content: [{ type: 'text', text: item.title }] }
    ]), ...(old?.content ?? [{ type: 'paragraph', content: [] }]));
    else body.push(old && content.sections?.items.find(s => s.id === item.id)?.title === item.title ? old.heading
      : { type: 'heading', attrs: { ...old?.heading.attrs, level: 2, sectionId: item.id }, content: [{ type: 'text', text: item.title }] },
      ...(old?.content ?? [{ type: 'paragraph', content: [] }]));
  }
  return { ...content, content: body, sections };
}
export function defineDocumentSections(content: SectionDocument, mode: 'headings' | 'whole', id: () => string): SectionDocument {
  if (content.sections) throw new Error('Sections have already been defined.');
  const items: DocumentSection[] = [];
  const body = content.content.map(node => {
    if (mode !== 'headings' || node.type !== 'heading' || node.attrs?.level !== 2) return node;
    const title = (node.content?.map(n => n.text ?? '').join('') || 'Untitled section').slice(0, 160);
    const sectionId = id(); items.push({ id: sectionId, title, hidden: false, protection: 'editable' });
    return { ...node, attrs: { ...node.attrs, sectionId } };
  });
  if (mode === 'whole') {
    const sectionId = id(), title = 'Document';
    items.push({ id: sectionId, title, hidden: false, protection: 'editable' });
    body.splice(0, body.length, ...content.content.map(n => n.type === 'heading' && n.attrs?.level === 2 ? { ...n, attrs: { ...n.attrs, level: 3 } } : n));
    body.unshift({ type: 'heading', attrs: { level: 2, sectionId }, content: [{ type: 'text', text: title }] });
  }
  if (!items.length) throw new Error('Add a top-level Heading 2 or choose the whole document.');
  if (items.length > 200) throw new Error('Use at most 200 sections.');
  return { ...content, content: body, sections: { schemaVersion: 1, structure: { ...sectionRules }, items } };
}
export function convertDocumentSections(content: SectionDocument): SectionDocument {
  validateDocumentSections(content);
  if (!content.sections || content.sections.boundaryMode) throw new Error('Choose existing heading sections to convert.');
  if (content.sections.items.some(s => s.protection !== 'editable' || s.hidden)) throw new Error('Make every section visible and editable before conversion.');
  // Retain original structural title nodes/marks for history and review anchors.
  return { ...content, sections: { ...content.sections, boundaryMode: 'independent' }, content: content.content.flatMap(node => {
    const sectionId = node.attrs?.sectionId;
    if (!sectionId) return [node];
    const { sectionId: _id, ...attrs } = node.attrs ?? {};
    return [{ type: 'sectionBoundary', attrs: { sectionId } }, { ...node, attrs: { ...attrs, sectionTitleId: sectionId } }];
  }) };
}
export function insertDocumentSection(content: SectionDocument, blockIndex: number, label: string, id: string): SectionDocument {
  validateDocumentSections(content);
  const binding = content.sections;
  if (!binding || binding.boundaryMode !== 'independent' || !binding.structure.allowAdd || binding.items.length >= 200) throw new Error('Use independent sections with adding enabled.');
  if (!Number.isInteger(blockIndex) || blockIndex < 0 || blockIndex > content.content.length || !label.trim() || label.length > 160) throw new Error('Choose a valid top-level block boundary and label.');
  let order = 0, owner: DocumentSection | undefined;
  content.content.forEach((node, index) => {
    if (node.type !== 'sectionBoundary') return;
    if (blockIndex === index + 1) throw new Error('Do not split a boundary and its structural label.');
    if (index < blockIndex) { order++; owner = binding.items.find(s => s.id === node.attrs?.sectionId); }
  });
  if (owner && (owner.hidden || owner.protection !== 'editable')) throw new Error('Choose a boundary in a visible editable section.');
  const items = [...binding.items]; items.splice(order, 0, { id, title: label.trim(), hidden: false, protection: 'editable' });
  const body = [...content.content]; body.splice(blockIndex, 0, { type: 'sectionBoundary', attrs: { sectionId: id } },
    { type: 'paragraph', attrs: { sectionTitleId: id }, content: [{ type: 'text', text: label.trim() }] }, { type: 'paragraph', content: [] });
  return { ...content, sections: { ...binding, items }, content: body };
}
/** Ordinary writers cannot change structure or structural labels, hidden/protected content. */
export function assertDocumentSectionWrite(before: SectionDocument, after: SectionDocument, separateId?: string) {
  validateDocumentSections(after);
  if (!same(before.template, after.template)) throw new Error('Use reviewed template controls to change reusable guidance or availability.');
  if (!same(before.sections, after.sections)) throw new Error('Use the explicit section controls to change the outline or protection.');
  if (separateId && !same(before.styles, after.styles)) throw new Error('A separate section edit cannot change document styles.');
  if (!before.sections) return;
  if (before.sections.items.some(section => section.hidden || section.protection !== 'editable') && !same(before.styles, after.styles)) throw new Error('Unlock protected sections before changing document-wide styles.');
  const old = splitDocumentSections(before), next = splitDocumentSections(after);
  for (const section of before.sections.items) {
    const a = old.parts.get(section.id), b = next.parts.get(section.id);
    if (!same(a?.prefix, b?.prefix)) throw new Error('Use the explicit section controls to change structural labels.');
    const immutable = section.hidden || section.protection === 'locked' || (section.protection === 'bulk_protected' && separateId !== section.id);
    if (immutable && !same(a, b)) throw new Error('Unlock this section or use its separate editor before changing its content.');
  }
  if (separateId) {
    if (!same(before.layout, after.layout) || !same(old.preamble, next.preamble)) throw new Error('A section edit cannot replace page tools or preamble.');
    for (const section of before.sections.items) if (section.id !== separateId && !same(old.parts.get(section.id), next.parts.get(section.id))) throw new Error('A section edit cannot change another section.');
  }
}

/** Explicit setup keeps ordinary headings unchanged when the whole body is selected. */
export function defineIndependentDocumentSections(content: SectionDocument, mode: 'headings' | 'whole', label: string, id: () => string): SectionDocument {
  if (content.sections) throw new Error('Sections have already been defined.');
  if (!label.trim() || label.length > 160) throw new Error('Choose an initial section label.');
  const items: DocumentSection[] = [], body: Node[] = [];
  const add = (title: string, original?: Node) => {
    const sectionId = id();
    items.push({ id: sectionId, title, hidden: false, protection: 'editable' });
    body.push({ type: 'sectionBoundary', attrs: { sectionId } }, original
      ? { ...original, attrs: { ...original.attrs, sectionTitleId: sectionId } }
      : { type: 'paragraph', attrs: { sectionTitleId: sectionId }, content: [{ type: 'text', text: title }] });
  };
  if (mode === 'whole' || content.content[0]?.type !== 'heading' || content.content[0]?.attrs?.level !== 2) add(label.trim());
  for (const node of content.content) {
    if (mode === 'headings' && node.type === 'heading' && node.attrs?.level === 2) {
      const title = node.content?.map(n => n.text ?? '').join('').trim() || 'Untitled section';
      if (title.length > 160) throw new Error('Section labels must be at most 160 characters.');
      add(title, node);
    } else body.push(node);
  }
  const result: SectionDocument = { ...content, content: body, sections: { schemaVersion: 1, boundaryMode: 'independent', structure: { ...sectionRules }, items } };
  if (items.length > 200) throw new Error('Use at most 200 sections.');
  validateDocumentSections(result);
  return result;
}
